import type { ReactNode } from "react"
import styles from "@/styles/components/RoundHeading.module.css"

/*
 * The two or three lines every round opens with.
 *
 * A room reads the heading once and then works; the `status` line is the part
 * that changes under them — how many have written, how many votes are left, how
 * many cards are still to place. Keeping it in the same block means the answer
 * to "where are we" is always in one place on every round.
 */

interface RoundHeadingProps {
  title: string
  objective?: string
  /** Keeps the objective on one line once the viewport has room for it. */
  objectiveNowrap?: boolean
  /** The live count for this beat. Rendered quietly, under the objective. */
  status?: ReactNode
  /** Sets `status` beside the objective instead of stacking it below. */
  statusInline?: boolean
  eyebrow?: string
  /** Right-aligned controls that belong to the round, not the session. */
  aside?: ReactNode
}

export default function RoundHeading({
  title,
  objective,
  objectiveNowrap,
  status,
  statusInline,
  eyebrow,
  aside,
}: RoundHeadingProps) {
  const objectiveEl = objective ? (
    <p
      className={
        objectiveNowrap ? `${styles.objective} ${styles.objectiveNowrap}` : styles.objective
      }
    >
      {objective}
    </p>
  ) : null

  return (
    <header className={styles.heading}>
      <div className={styles.text}>
        {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        <h1 className={styles.title}>{title}</h1>
        {statusInline ? (
          <div className={styles.objectiveRow}>
            {objectiveEl}
            {status ? <p className={styles.status}>{status}</p> : null}
          </div>
        ) : (
          <>
            {objectiveEl}
            {status ? <p className={styles.status}>{status}</p> : null}
          </>
        )}
      </div>
      {aside ? <div className={styles.aside}>{aside}</div> : null}
    </header>
  )
}
